import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog } from 'primereact/dialog'
import { Messages } from 'primereact/messages'
import { ProgressBar } from 'primereact/progressbar'

import { useToastContext } from '../../../../contexts'
import { useBackgroundTaskStatus } from '../../../../data/batchstatus/hooks'
import { BackgroundTaskPhase } from '../../../../data/batchstatus/models'
import { calculateBreakpointWidth } from '../../../../utils/helper/calculateBreakpointWidth'
import { Progressbar } from './Progressbar'

interface Props {
  locationUrl: string
  visible: boolean
  onHide: () => void
  isDelete: boolean
  isCreate: boolean
  isSupplierProcess: boolean
  mutate: () => void
  retry?: string
}

export const BackgroundTaskStatusDialog: React.FC<Props> = ({
  visible,
  onHide,
  locationUrl,
  isDelete,
  isCreate,
  isSupplierProcess,
  mutate,
  retry,
}) => {
  const { t } = useTranslation()

  const { data: backgroundStatus, mutate: mutateBackroundTask } =
    useBackgroundTaskStatus(locationUrl, retry)

  const msgs = useRef<Messages>(null)

  const { replaceToast } = useToastContext()

  if (backgroundStatus?.phase === BackgroundTaskPhase.Completed) {
    replaceToast({
      severity: 'success',
      summary: isCreate ? (
        <>
          {!isSupplierProcess
            ? t(
                'roleAssignments.table.backroundTask.create.messages.success.summary',
              )
            : t(
                'suppliers.table.backroundTask.create.messages.success.summary',
              )}
        </>
      ) : (
        <>
          {!isSupplierProcess
            ? t(
                'roleAssignments.table.backroundTask.delete.messages.success.summary',
              )
            : t(
                'suppliers.table.backroundTask.delete.messages.success.summary',
              )}
        </>
      ),
      detail: isCreate ? (
        <>
          {!isSupplierProcess
            ? t(
                'roleAssignments.table.backroundTask.create.messages.success.detail',
                { total: backgroundStatus.totalItems },
              )
            : t(
                'suppliers.table.backroundTask.create.messages.success.detail',
                { total: backgroundStatus.totalItems },
              )}
        </>
      ) : (
        <>
          {!isSupplierProcess
            ? t(
                'roleAssignments.table.backroundTask.delete.messages.success.detail',
                { total: backgroundStatus.totalItems },
              )
            : t(
                'suppliers.table.backroundTask.delete.messages.success.detail',
                { total: backgroundStatus.totalItems },
              )}
        </>
      ),
      id: backgroundStatus.taskId,
    })
    if (backgroundStatus.errors.length === 0) onHide()

    mutate()
  }

  if (backgroundStatus?.phase === BackgroundTaskPhase.Cancelled) {
    replaceToast({
      severity: 'warn',
      summary: isCreate ? (
        <>
          {!isSupplierProcess
            ? t('roleAssignments.table.backroundTask.titleCreate')
            : t('suppliers.table.backroundTask.titleCreate')}
        </>
      ) : (
        <>
          {!isSupplierProcess
            ? t('roleAssignments.table.backroundTask.titleDelete')
            : t('suppliers.table.backroundTask.titleDelete')}
        </>
      ),
      detail: (
        <>
          {isSupplierProcess
            ? t('suppliers.table.backroundTask.cancel.label')
            : t('roleAssignments.table.backroundTask.cancel.label')}
        </>
      ),
      life: 10000,
      id: backgroundStatus.taskId,
    })
    if (backgroundStatus.errors.length === 0) onHide()

    mutate()
  }

  if (backgroundStatus && backgroundStatus?.errors) {
    msgs.current?.clear()
    backgroundStatus?.errors.forEach(x => {
      msgs.current?.show({
        sticky: true,
        severity: 'error',
        summary: t('roleAssignments.table.backroundTask.error'),
        detail: x.errorMessage,
      })
    })
  }

  const header = isCreate ? (
    <>
      {!isSupplierProcess
        ? t('roleAssignments.table.backroundTask.titleCreate')
        : t('suppliers.table.backroundTask.titleCreate')}
    </>
  ) : (
    <>
      {!isSupplierProcess
        ? t('roleAssignments.table.backroundTask.titleDelete')
        : t('suppliers.table.backroundTask.titleDelete')}
    </>
  )

  mutateBackroundTask()

  return (
    <Dialog
      header={header}
      style={{ width: '30rem' }}
      breakpoints={{ [calculateBreakpointWidth(30)]: '90vw' }}
      visible={visible}
      onHide={() => {
        if (backgroundStatus?.errors) onHide()
      }}
      closable={
        backgroundStatus?.phase !== BackgroundTaskPhase.InProgress &&
        backgroundStatus?.errors.length !== 0
          ? true
          : false
      }
    >
      <div className="formgrid grid">
        <Progressbar
          isCreate={isCreate}
          isDelete={isDelete}
          isSupplierProcess={isSupplierProcess}
          backgroundStatus={backgroundStatus}
          visible={visible}
        />
        <Messages className="col-12" ref={msgs} />
      </div>
    </Dialog>
  )
}
